import Pressable from '@/components/motion/pressable';
import ChangeCommentModal from '@/components/change-comment-modal';
import { useOpenClient } from '@/hooks/use-open-client';
import VbivStatus from '@/components/vbiv-status';
import ClientCards from '@/components/client/client-cards';
import ClientPhones from '@/components/client/client-phones';
import type { IClient } from '@/entities/client.entity';
import { CalendarClockIcon, ContainerIcon, HashIcon, LayoutGridIcon, MessageCircleIcon, UserIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useState, type MouseEvent } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

const ClientCard = memo(function ClientCard({ client }: { client: IClient }) {
	const [commentText, setCommentText] = useState(client.comment ?? '');

	const { openClient } = useOpenClient();

	const goToClient = useCallback(() => {
		openClient(client.id, { listType: 'clients', clientId: client.id, itemId: client.id });
	}, [client.id, openClient]);

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	function handleCopyText(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	useEffect(() => {
		if (client.comment !== commentText) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setCommentText(client.comment || '');
		}
	}, [client.comment]);

	return (
		<Pressable
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/30'
			style={{
				borderLeftColor: client.isOnline ? 'var(--chart-2)' : 'var(--chart-5)',
			}}
			onClick={goToClient}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToClient();
				}
			}}
		>
			<div className='flex justify-between items-center'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<MessageCircleIcon size={16} className='opacity-40' />
					</div>
					<span className='flex break-all items-center' onClick={e => e.stopPropagation()}>
						{client.comment && (
							<span className='text-sm hover:bg-accent cursor-pointer select-none text-[#e89bff]' onClick={e => handleCopy(e, client.comment || '')}>
								{client.comment}
							</span>
						)}
						<ChangeCommentModal currentComment={client.comment} client={client} />
					</span>
				</span>

				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={client.id} takeStatus={client.takeStatus} takenFrom={client.takenFrom} />
				</div>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + client.id)}>
						{client.id}
					</span>
				</span>
				<span className='text-sm pl-2 flex flex-col items-end gap-0.5'>
					{client.pendingLaunch ? <span className='text-xs text-amber-500'>Ожидает launch</span> : null}
					{client.isOnline ? (
						<span className='text-chart-2 flex items-center gap-1'>
							<div className='bg-chart-2 w-2 h-2 animate-pulse rounded-full'></div>Онлайн
						</span>
					) : (
						<span className='text-chart-5'>Офлайн</span>
					)}
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.buildId)}>
						{client.buildId}
					</span>
					<div className='w-[18px]'>
						<LayoutGridIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.sdkVersion)}>
						{client.sdkVersion}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<UserIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, client.profileNickname)}>
						{client.profileNickname}
					</span>
				</span>
			</div>

			<div
				className='mt-1 pt-1.5 border-t border-border/50 flex flex-col gap-2'
				onClick={e => e.stopPropagation()}
			>
				<ClientPhones phones={client.phones} onCopy={handleCopyText} variant='compact' />
				<ClientCards
					cards={client.cards?.filter(Boolean) ?? []}
					phones={client.phones}
					cardFillJobs={client.cardFillJobs}
					onCopy={handleCopyText}
					variant='compact'
				/>
			</div>

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40 '>Установлен</span>
				<div className=' text-sm text-chart-2 opacity-95'>
					<TimeAgo date={client.createdAt} formatter={formatter} />
				</div>
			</span>
		</Pressable>
	);
});

export default ClientCard;
